<template>
  <div v-show="uploadStore.hasDocuments" class="selected-documents-table">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <Button
          :disabled="!uploadStore.hasDocuments || uploadStore.isUploading"
          :loading="uploadStore.isUploading"
          :label="$t('views.uploadDocuments.actions.uploadAll')"
          icon="pi pi-upload"
          severity="gradient-accent-blue"
          size="small"
          @click="uploadStore.uploadDocuments"
        />
        <Button
          class="ms-2"
          :disabled="!uploadStore.hasDocuments"
          :label="$t('views.uploadDocuments.actions.clearAll')"
          icon="pi pi-trash"
          severity="danger"
          size="small"
          @click="uploadStore.clearDocuments"
        />
      </div>
      <div class="d-flex align-items-center gap-2">
        <label for="rows-per-page custom-font-size mb-1"
          >{{ $t("views.uploadDocuments.table.rowsPerPage") }}:</label
        >
        <Select
          id="rows-per-page"
          v-model="rows"
          class="w-auto"
          :options="rowsPerPageOptions"
          option-label="label"
          option-value="value"
          :placeholder="rows.toString()"
          size="small"
        />
      </div>
    </div>

    <div class="p-1 bg-white rounded-4 compact-paginator">
      <DataTable
        v-model:selection="uploadStore.selectedDocuments"
        :value="uploadStore.documents"
        :paginator="true"
        :rows="rows"
        response-layout="scroll"
        data-key="id"
        class="p-datatable-sm"
      >
        <template #empty>
          <div class="text-center p-4">
            <i class="pi pi-file text-3xl mb-3" style="color: #dee2e6"></i>
            <p>{{ $t("views.uploadDocuments.table.noDocuments") }}</p>
          </div>
        </template>

        <Column
          field="name"
          :header="$t('views.uploadDocuments.table.filename')"
          header-class="p-2"
          body-class="p-2"
        />

        <Column
          field="size"
          :header="$t('views.uploadDocuments.table.size')"
          header-class="p-2"
          body-class="p-2"
        >
          <template #body="slotProps">
            {{ formatFileSize(slotProps.data.size) }}
          </template>
        </Column>

        <Column
          field="status"
          :header="$t('views.uploadDocuments.table.statusColName')"
          header-class="p-2"
          body-class="p-2"
        >
          <template #body="slotProps">
            <Badge
              :value="
                $t(`views.uploadDocuments.status.${slotProps.data.status}`)
              "
              size="small"
              class="status-badge"
              :pt="{
                root: {
                  style: {
                    backgroundColor: getStatusSeverity(slotProps.data.status),
                    color: getStatusTextColor()
                  }
                }
              }"
            />
          </template>
        </Column>

        <Column
          :header="$t('views.uploadDocuments.table.progress')"
          field="uploadProgress"
          header-class="p-2"
          body-class="p-2"
        >
          <template #body="slotProps">
            <ProgressBar
              v-if="slotProps.data.uploadProgress > 0"
              :value="slotProps.data.uploadProgress"
              :style="{ height: '0.5rem' }"
            />
          </template>
        </Column>

        <Column
          :header="$t('views.uploadDocuments.table.actions')"
          style="width: 100px"
          header-class="p-2"
          body-class="p-2"
        >
          <template #body="slotProps">
            <Button
              class="p-button-text"
              severity="danger"
              rounded
              icon="pi pi-trash"
              size="small"
              @click="uploadStore.removeDocument(slotProps.data.id)"
            />
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue"
import { useUploadStore } from "@/store"
import { useI18n } from "vue-i18n"
import {
  getStatusSeverity,
  getStatusTextColor
} from "@/composables/useStatusUtils"

const { t } = useI18n()
const uploadStore = useUploadStore()
const rows = ref(10)
const rowsPerPageOptions = ref([
  { value: 5, label: t("views.uploadDocuments.table.perPageOptions.five") },
  { value: 10, label: t("views.uploadDocuments.table.perPageOptions.ten") },
  { value: 20, label: t("views.uploadDocuments.table.perPageOptions.twenty") },
  { value: 50, label: t("views.uploadDocuments.table.perPageOptions.fifty") }
])

const formatFileSize = (bytes) => {
  if (bytes === 0) return t("views.uploadDocuments.fileSize.zeroSize")
  const k = 1024
  const sizes = [
    t("views.uploadDocuments.fileSize.bytes"),
    t("views.uploadDocuments.fileSize.kb"),
    t("views.uploadDocuments.fileSize.mb"),
    t("views.uploadDocuments.fileSize.gb")
  ]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
}
</script>
